import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  BarChart3,
  FileBarChart,
  HeartHandshake,
  TrendingDown,
  TrendingUp,
  UserRoundSearch,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getReportSummary,
} from "@/api/reporting";
import { AnimatedNumber } from "@/components/ui/AnimatedNumber";
import { Card } from "@/components/ui/card";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageContainer } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";

const periodOptions = [
  { label: "Last 30 Days", value: 30 },
  { label: "Last 60 Days", value: 60 },
  { label: "Last 90 Days", value: 90 },
  { label: "Last 6 Months", value: 180 },
  { label: "Last Year", value: 365 },
];

export function ReportsPage() {
  const { churchId } = useAuth();
  const { permissions } = useEffectivePermissions();
  const [days, setDays] = useState(30);

  const reportQuery = useQuery({
    queryKey: ["report-summary", churchId, days],
    enabled: !!churchId,
    queryFn: () => getReportSummary(churchId!, days),
  });

  if (reportQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (
    reportQuery.isError ||
    !reportQuery.data
  ) {
    return (
      <PageContainer>
        <QueryErrorState
          title="Unable to load reports"
          description="MinistryIQ couldn't load reporting data. Check your connection and try again."
          onRetry={() => void reportQuery.refetch()}
        />
      </PageContainer>
    );
  }

  const report = reportQuery.data;
  const hasAttendanceData = report.attendance_trend.some(
    (point) => point.present > 0,
  );

  if (!hasAttendanceData) {
    return (
      <PageContainer>
        <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/95 to-indigo-950/30 px-7 py-8"><p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-400">Reports</p><h1 className="mt-3 text-3xl font-bold text-white">Reporting Center</h1></section>
        <div className="mt-7"><EmptyState icon={<FileBarChart size={24} />} title="Reports need attendance history" description="Once attendance is recorded, MinistryIQ will turn it into useful trends and ministry insights. Empty charts are hidden until then." action={permissions.has("ATTENDANCE_VIEW") && <Link to="/attendance" className="rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-400">Record Attendance</Link>} /></div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-indigo-950/30 px-7 py-8 shadow-xl shadow-black/20">
        <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-400">
                Reports
              </p>

              {reportQuery.isFetching && (
                <p
                  role="status"
                  className="text-xs font-medium text-indigo-300"
                >
                  Refreshing reports...
                </p>
              )}
            </div>

            <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Reporting Center
            </h1>

            <p className="mt-3 max-w-2xl text-base leading-7 text-slate-300">
              Understand ministry health and explore deeper
              operational insights.
            </p>
          </div>

          <select
            value={days}
            onChange={(event) =>
              setDays(Number(event.target.value))
            }
            className="min-w-[210px] rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none transition focus:border-indigo-500"
          >
            {periodOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      <div className="mt-7 grid gap-4 md:grid-cols-3">
        <SummaryCard
          icon={<Users size={23} />}
          label="Average Attendance"
          value={report.average_attendance}
          description="Present check-ins per eligible service"
        />

        <AttendanceChangeCard
          value={report.attendance_change}
        />

        <SummaryCard
          icon={<FileBarChart size={23} />}
          label="Active Reports"
          value={1}
          description="1 of 4 reports available"
        />
      </div>

      <div className="mt-7">
        <Card className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-white">
                Attendance Snapshot
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Present check-ins across eligible services
              </p>
            </div>

            <Link
              to="/reports/attendance"
              className="flex items-center gap-2 text-sm font-semibold text-indigo-400 hover:text-indigo-300"
            >
              View Attendance Report
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={report.attendance_trend}
                margin={{
                  top: 12,
                  right: 12,
                  left: -18,
                  bottom: 0,
                }}
              >
                <defs>
                  <linearGradient
                    id="reportSnapshotGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor="#818cf8"
                      stopOpacity={0.4}
                    />

                    <stop
                      offset="95%"
                      stopColor="#818cf8"
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  stroke="#1e293b"
                  strokeDasharray="4 4"
                  vertical={false}
                />

                <XAxis
                  dataKey="label"
                  stroke="#64748b"
                  tickLine={false}
                  axisLine={false}
                />

                <YAxis
                  stroke="#64748b"
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />

                <Tooltip
                  contentStyle={{
                    background: "#020617",
                    border: "1px solid #1e293b",
                    borderRadius: "14px",
                    color: "#fff",
                  }}
                />

                <Area
                  type="monotone"
                  dataKey="present"
                  name="Present"
                  stroke="#818cf8"
                  strokeWidth={3}
                  fill="url(#reportSnapshotGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="mt-7">
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-white">
            Available Reports
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Explore deeper ministry trends and operational insights.
          </p>

          <div className="mt-6 space-y-3">
            <ReportRow
              icon={<BarChart3 size={21} />}
              title="Attendance Report"
              description="Service attendance, check-ins, and attendance trends."
              to="/reports/attendance"
            />

            <ReportRow
              icon={<UserRoundSearch size={21} />}
              title="Member Engagement"
              description="Attendance consistency, missed services, and engagement patterns."
            />

            <ReportRow
              icon={<Users size={21} />}
              title="Visitor Report"
              description="Visitor attendance, return visits, and conversion trends."
            />

            <ReportRow
              icon={<HeartHandshake size={21} />}
              title="Care Activity"
              description="Follow-up workload, care activity, and completion trends."
            />
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  description,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  description: string;
}) {
  return (
    <Card className="p-6">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-400">
        {icon}
      </div>

      <p className="mt-5 text-sm font-medium text-slate-400">
        {label}
      </p>

      <AnimatedNumber
        value={value}
        className="mt-2 block text-4xl font-bold text-white"
      />

      <p className="mt-3 text-xs text-slate-600">
        {description}
      </p>
    </Card>
  );
}

function AttendanceChangeCard({
  value,
}: {
  value: number | null;
}) {
  const negative = value !== null && value < 0;
  const Icon = negative ? TrendingDown : TrendingUp;

  return (
    <Card className="p-6">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
        <Icon size={23} />
      </div>

      <p className="mt-5 text-sm font-medium text-slate-400">
        Attendance Change
      </p>

      <p className="mt-2 text-4xl font-bold text-white">
        {value === null
          ? "-"
          : `${value > 0 ? "+" : ""}${value}%`}
      </p>

      <p className="mt-3 text-xs text-slate-600">
        {value === null
          ? "No prior period baseline"
          : "Compared with previous equal period"}
      </p>
    </Card>
  );
}

function ReportRow({
  icon,
  title,
  description,
  to,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  to?: string;
}) {
  const content = (
    <>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-400">
        {icon}
      </div>

      <div className="min-w-0 flex-1">
        <p className="font-semibold text-white">
          {title}
        </p>

        <p className="mt-1 text-sm text-slate-500">
          {description}
        </p>
      </div>

      {to ? (
        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-400">
          View Report
          <ArrowRight size={16} />
        </div>
      ) : (
        <span className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-500">
          Coming Soon
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="group flex items-center gap-4 rounded-xl border border-slate-800 bg-slate-950/40 p-4 transition hover:border-indigo-500/30 hover:bg-slate-900"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-4 rounded-xl border border-slate-800 bg-slate-950/40 p-4">
      {content}
    </div>
  );
}

